import type { components as StrapiComponents } from '@core/types/clients/strapi'
import { useContentPage } from '#imports'

/**
 * Picks Nuxt Content or Strapi for each page-level data fetch using
 * `useContentMigrationFlag('<key>')` per collection. Both queries are
 * instantiated to satisfy the cego/require-enrich-return lint rule, but only
 * the one matching the flag actually runs (the other has `enabled: false`).
 */

function normalizeMedia(media?: { url: string, thumbnailUrl?: string }) {
    if (!media) return undefined
    return {
        url: media.url,
        formats: {
            thumbnail: {
                url: media.thumbnailUrl || media.url,
            },
        },
    }
}

export function usePageData(seoName: string | undefined) {
    const { getPageBySeoName } = useContentPage()
    const strapi = useStrapi()
    const { site } = useCoreConfig()

    const contentQuery = useQuery(async () => {
        if (!seoName) return null
        const page = await getPageBySeoName(seoName)
        return page as unknown as StrapiComponents['schemas']['Page']
    }, {
        key: () => `content:page-${seoName}`,
        enabled: () => useContentMigrationFlag('pages') && !!seoName,
        lazy: false,
        server: true,
    })

    const strapiQuery = useQuery(async () => {
        if (!seoName) return null
        const { data } = await strapi.GET('/pages/{seoName}', {
            params: { path: { seoName }, query: { brand: site.brand } },
        })
        return data as StrapiComponents['schemas']['Page']
    }, {
        key: () => `strapi:page-${seoName}`,
        enabled: () => !useContentMigrationFlag('pages') && !!seoName,
        lazy: false,
        server: true,
    })

    if (useContentMigrationFlag('pages')) return contentQuery
    return strapiQuery
}

export function useFaqData() {
    const { getFaqCollections } = useContentFaq()

    return useQuery(async () => {
        const collections = await getFaqCollections()
        const sections = collections?.[0]?.faqSections || []
        return sections.length ? sections : null
    }, {
        key: 'content:faq',
        enabled: () => useContentMigrationFlag('faq'),
        lazy: false,
        server: true,
    })
}

export function useConsentData(slug: string) {
    const { getConsentBySlug } = useContentConsent()
    const { getPrivacyPolicy } = useStrapiConsent()

    const contentQuery = useQuery(async () => {
        return await getConsentBySlug(slug)
    }, {
        key: () => `content:consent-${slug}`,
        enabled: () => useContentMigrationFlag('consents'),
        lazy: false,
        server: true,
    })

    const strapiQuery = getPrivacyPolicy({
        lazy: false,
        server: true,
        enabled: () => !useContentMigrationFlag('consents'),
    })

    if (useContentMigrationFlag('consents')) return contentQuery
    return strapiQuery
}

export function useGeneralConsentData() {
    const { getGeneralConsents } = useContentGeneralConsent()
    const { getGeneralTerms } = useStrapiConsent()

    const contentQuery = useQuery(async () => {
        const consents = await getGeneralConsents()
        return consents?.length ? consents[0] : null
    }, {
        key: 'content:general-consent',
        enabled: () => useContentMigrationFlag('generalConsents'),
        lazy: false,
        server: true,
    })

    const strapiQuery = getGeneralTerms({
        lazy: false,
        server: true,
        enabled: () => !useContentMigrationFlag('generalConsents'),
    })

    if (useContentMigrationFlag('generalConsents')) return contentQuery
    return strapiQuery
}

// TODO: Re-enable when blog content migration is ready
// export function useBlogData() { ... }
// export function useBlogPostData(slug: string) { ... }

export function usePressData() {
    const { getPressArticles } = useContentPress()

    return useQuery(async () => {
        const articles = await getPressArticles()
        if (!articles?.length) return null
        return articles.map(article => ({
            ...article,
            publishAt: article.publishAt
                ? { publishAt: article.publishAt }
                : undefined,
            media: normalizeMedia(article.media),
        }))
    }, {
        key: 'content:press',
        enabled: () => useContentMigrationFlag('press'),
        lazy: false,
        server: true,
    })
}

export function useGamePageData(seoName: string) {
    const { getGamePageBySeoName } = useContentGamePage()
    const strapi = useStrapi()
    const { site } = useCoreConfig()

    const contentQuery = useQuery(async () => {
        const page = await getGamePageBySeoName(seoName)
        return page as unknown as StrapiComponents['schemas']['GamePage']
    }, {
        key: () => `content:game-page-${seoName}`,
        enabled: () => useContentMigrationFlag('gamePages'),
        lazy: false,
        server: true,
    })

    const strapiQuery = useQuery(async () => {
        const { data, error } = await strapi.GET('/game-pages/{seoName}', {
            params: { path: { seoName }, query: { brand: site.brand } },
        })
        if (error) throw error
        return data as StrapiComponents['schemas']['GamePage']
    }, {
        key: () => `strapi:game-page-${seoName}`,
        enabled: () => !useContentMigrationFlag('gamePages'),
        lazy: false,
        server: true,
    })

    if (useContentMigrationFlag('gamePages')) return contentQuery
    return strapiQuery
}

// NOTE: dk also exposes useGamePagesListData and useGamePageOverviewData wrappers,
// but their Strapi paths reference shapes/endpoints that no longer exist in the
// current core strapi schema. UK has no consumers of these wrappers
// today, so they are intentionally omitted here. Reintroduce when consumers
// exist and the Strapi shape is reconciled.

export function usePressArticleData(slug: string) {
    const { getPressArticleBySlug } = useContentPress()

    return useQuery(async () => {
        const article = await getPressArticleBySlug(slug)
        if (!article) return null
        return {
            ...article,
            publishAt: article.publishAt
                ? { publishAt: article.publishAt }
                : undefined,
            media: normalizeMedia(article.media),
        }
    }, {
        key: () => `content:press-article-${slug}`,
        enabled: () => useContentMigrationFlag('press'),
        lazy: false,
        server: true,
    })
}
